import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { fetchArchonAccess } from '../services/ArchonDataApiService';

interface ArchonAccessContextValue {
  /** null until the first access check answers. */
  canManageData: boolean | null;
  /** Set when the last access check failed, so a failure never reads as a refusal. */
  error: string | null;
  retry: () => void;
}

const ArchonAccessContext = createContext<ArchonAccessContextValue>({
  canManageData: null,
  error: null,
  retry: () => undefined,
});

export const ArchonAccessProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { token } = useAuthContext();
  const [canManageData, setCanManageData] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  // A renewed token re-checks without clearing the last answer, so an open
  // owner page is not unmounted (and its drafts lost) while the check runs.
  useEffect(() => {
    if (!token) {
      setCanManageData(false);
      return undefined;
    }

    let active = true;
    void fetchArchonAccess(token)
      .then((response) => {
        if (!active) return;
        setCanManageData(response.canManageData);
        setError(null);
      })
      .catch((fetchError: unknown) => {
        if (!active) return;
        setError(fetchError instanceof Error ? fetchError.message : 'Access check failed');
      });

    return () => {
      active = false;
    };
  }, [token, attempt]);

  return (
    <ArchonAccessContext.Provider value={{ canManageData, error, retry }}>
      {children}
    </ArchonAccessContext.Provider>
  );
};

export function useArchonAccess(): ArchonAccessContextValue {
  return useContext(ArchonAccessContext);
}
